import React from 'react'
import Box from '@mui/material/Box'
import Modal from '@/v2/components/common/Modal'
import TextField from '@/v2/components/common/TextField'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import {
  useSettingsRooms,
  useAddRoom,
  useUpdateRoom,
  useDeleteRoom,
} from '@/v2/hooks/settings/useSettingsRooms'
import useFormModal from '@/v2/hooks/settings/useFormModal'
import {
  SettingsPage,
  SettingsEmptyState,
  PrimaryButton,
  CardGrid,
  Card,
  CardTitle,
  CardDescription,
  CardActions,
  ActionButton,
} from '@/v2/components/settings/shared'

const Rooms = () => {
  const { rooms, isLoading } = useSettingsRooms()
  const { addRoom, isPending: isAdding } = useAddRoom()
  const { updateRoom, isPending: isUpdating } = useUpdateRoom()
  const { deleteRoom, isPending: isDeleting } = useDeleteRoom()
  const { onOpenSnackbar } = useSnackbarContext()

  const {
    showModal,
    editingItem,
    formData,
    isEditing,
    openForCreate,
    openForEdit,
    close,
    updateField,
  } = useFormModal({ title: '', description: '' })

  const isSaving = isAdding || isUpdating

  const handleDelete = async roomId => {
    if (confirm('Are you sure you want to delete this room?')) {
      try {
        await deleteRoom(roomId)
        onOpenSnackbar('Room deleted successfully', 'success')
        close()
      } catch (error) {
        onOpenSnackbar('Failed to delete room', 'error')
      }
    }
  }

  const handleSubmit = async () => {
    const roomPayload = {
      title: formData.title,
      description: formData.description,
    }

    try {
      if (isEditing) {
        await updateRoom({ id: editingItem.id, ...roomPayload })
        onOpenSnackbar('Room updated successfully', 'success')
      } else {
        await addRoom(roomPayload)
        onOpenSnackbar('Room created successfully', 'success')
      }
      close()
    } catch (error) {
      onOpenSnackbar('Failed to save room', 'error')
    }
  }

  return (
    <SettingsPage
      title="Rooms"
      subtitle="Manage treatment rooms and consultation spaces"
      loading={isLoading}
      actions={
        <PrimaryButton onClick={openForCreate}>+ Add Room</PrimaryButton>
      }
    >
      {rooms.length === 0 ? (
        <SettingsEmptyState
          icon={
            <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2v0z" />
              <path d="M9 9v6m6-6v6" />
            </svg>
          }
          heading="You haven't added any rooms yet"
          description={
            <>
              Click on <strong>Add room</strong> to create the first one
            </>
          }
          actionLabel="Add room"
          onAction={openForCreate}
        />
      ) : (
        <CardGrid>
          {rooms.map(room => (
            <Card key={room.id} onClick={() => openForEdit(room)}>
              <CardTitle>{room.title}</CardTitle>
              <CardDescription>{room.description}</CardDescription>
              <CardActions onClick={e => e.stopPropagation()}>
                <ActionButton onClick={() => openForEdit(room)}>
                  Edit
                </ActionButton>
                <ActionButton
                  $variant="danger"
                  onClick={() => handleDelete(room.id)}
                >
                  Delete
                </ActionButton>
              </CardActions>
            </Card>
          ))}
        </CardGrid>
      )}

      <Modal
        isOpen={showModal}
        title={isEditing ? 'Edit Room' : 'Add Room'}
        onClose={close}
        onConfirm={handleSubmit}
        confirmLabel={isEditing ? 'Save Changes' : 'Add Room'}
        cancelLabel="Cancel"
        submitDisabled={isSaving}
        loading={false}
        maxWidth="sm"
        fullWidth
        showExtraButton={isEditing}
        extraButtonLabel="Delete Room"
        extraButtonColor="error"
        onExtraButtonClick={() => handleDelete(editingItem?.id)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Room Title"
            placeholder="E.g.: Room #1"
            value={formData.title}
            onChange={val => updateField('title', val)}
          />
          <TextField
            fullWidth
            label="Description"
            placeholder="E.g.: Consultation room with dental chairs and other equipment"
            value={formData.description}
            onChange={val => updateField('description', val)}
          />
        </Box>
      </Modal>
    </SettingsPage>
  )
}

export default Rooms
